import { faCircleCheck, faHeadset } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	IssueType,
	PORTAL_ISSUE_MESSAGE_MAX_LENGTH,
	PORTAL_ISSUE_SUBJECT_MAX_LENGTH,
	PORTAL_ISSUE_TYPES,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { submitPortalIssue } from '../../../services/portalIssueService';

const INPUT_CLASSES =
	'w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100';

/**
 * Submit-an-Issue widget (PD-13705) — a compact form for signed-in
 * customers to send a categorized question / request / problem report
 * to the support team. The submission is recorded as an Issue in the
 * CRM, linked to the customer's active client and assigned to their
 * sales rep. On success the created issue id is surfaced as a
 * reference number.
 *
 * Login-required — the dashboard keeps it off anonymous layouts via
 * AUTH_ONLY_WIDGET_TYPES; the token guard here is the backstop.
 */
export function SubmitIssueWidget({
	title,
}: {
	title?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();

	const [type, setType] = useState<IssueType>(IssueType.QUESTION);
	const [subject, setSubject] = useState('');
	const [message, setMessage] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [submittedIssueId, setSubmittedIssueId] = useState<number | null>(null);

	if (token == null) {
		return null;
	}

	const handleSubmit = async (): Promise<void> => {
		const trimmedSubject = subject.trim();
		const trimmedMessage = message.trim();

		if (trimmedSubject.length === 0 || trimmedMessage.length === 0) {
			setError(t('Please fill in a subject and a message.'));
			return;
		}

		setSubmitting(true);
		setError(null);

		try {
			const result = await submitPortalIssue(token, {
				subject: trimmedSubject,
				type,
				message: trimmedMessage,
			});
			setSubmittedIssueId(result.issueId);
			setSubject('');
			setMessage('');
			setType(IssueType.QUESTION);
		} catch {
			setError(t("We couldn't send your request right now. Please try again."));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section className="w-full h-full flex flex-col bg-brand-surface border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center gap-2 mb-3">
				<FontAwesomeIcon icon={faHeadset} className="text-portal-accent" />
				<h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					{t(title ?? 'Submit an issue')}
				</h2>
			</header>

			{submittedIssueId != null ? (
				<div className="flex-1 flex flex-col items-center justify-center text-center gap-2 py-6">
					<FontAwesomeIcon
						icon={faCircleCheck}
						className="text-3xl text-portal-accent"
					/>
					<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
						{t('Your request has been submitted.')}
					</p>
					<p className="text-xs text-brand-muted">
						{t('Reference')} {t('#')}
						{submittedIssueId}
						{' — '}
						{t('our team will follow up with you.')}
					</p>
					<button
						type="button"
						onClick={() => {
							setSubmittedIssueId(null);
						}}
						className="mt-2 text-xs md:text-sm text-portal-accent hover:opacity-80"
					>
						{t('Submit another issue')}
					</button>
				</div>
			) : (
				<form
					className="flex-1 flex flex-col gap-3"
					onSubmit={(e) => {
						e.preventDefault();
						void handleSubmit();
					}}
				>
					<label className="block">
						<span className="block text-xs font-medium text-brand-muted mb-1">
							{t('Category')}
						</span>
						<select
							value={type}
							onChange={(e) => {
								setType(e.target.value as IssueType);
							}}
							disabled={submitting}
							className={INPUT_CLASSES}
						>
							{PORTAL_ISSUE_TYPES.map((issueType) => (
								<option key={issueType} value={issueType}>
									{t(issueType)}
								</option>
							))}
						</select>
					</label>

					<label className="block">
						<span className="block text-xs font-medium text-brand-muted mb-1">
							{t('Subject')}
						</span>
						<input
							type="text"
							value={subject}
							onChange={(e) => {
								setSubject(e.target.value);
							}}
							maxLength={PORTAL_ISSUE_SUBJECT_MAX_LENGTH}
							placeholder={t('What is this about?')}
							disabled={submitting}
							className={INPUT_CLASSES}
						/>
					</label>

					<label className="block flex-1 flex flex-col">
						<span className="block text-xs font-medium text-brand-muted mb-1">
							{t('Message')}
						</span>
						<textarea
							value={message}
							onChange={(e) => {
								setMessage(e.target.value);
							}}
							maxLength={PORTAL_ISSUE_MESSAGE_MAX_LENGTH}
							placeholder={t('Tell us what you need help with...')}
							disabled={submitting}
							rows={4}
							className={`${INPUT_CLASSES} flex-1 resize-none min-h-[96px]`}
						/>
					</label>

					{error != null && (
						<p className="text-xs text-red-600 dark:text-red-400">{error}</p>
					)}

					<button
						type="submit"
						disabled={submitting}
						className="w-full px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50"
					>
						{submitting ? t('Sending...') : t('Send')}
					</button>
				</form>
			)}
		</section>
	);
}
